import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { getProducts, getEvents, getImageUrl } from "@/lib/api";
import { useStore } from "@/lib/store";
import ProductCard from "@/components/ProductCard";
import SizzhkaLoader from "@/components/SizzhkaLoader";

const HERO_IMG = "https://images.unsplash.com/photo-1582284834275-c4651b85b3fe?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const { formatPrice, t, storeName, storeDescription } = useStore();

  useEffect(() => {
    Promise.all([
      getProducts({ limit: 8, new_arrivals: "true" }),
      getEvents({ limit: 5 }),
    ]).then(([prodData, evData]) => {
      setProducts(prodData.products || []);
      setEvents(evData.events || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <SizzhkaLoader />;
  }

  return (
    <div className="animate-fade-in" data-testid="home-page">
      {/* Hero */}
      <section className="relative w-full aspect-[16/10] md:aspect-[21/9] overflow-hidden" data-testid="hero-section">
        <img src={HERO_IMG} alt="Hero" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full p-4 md:p-8">
          <h1 className="font-display text-3xl sm:text-4xl md:text-6xl font-bold tracking-tight">
            {storeName}
          </h1>
          <p className="text-zinc-400 text-xs md:text-sm mt-2 max-w-md">
            {storeDescription}
          </p>
          <Link to="/catalog"
            className="inline-flex items-center gap-2 mt-4 px-6 h-10 bg-white text-black text-xs font-display font-bold uppercase tracking-wider hover:bg-zinc-200 transition-colors"
            data-testid="hero-cta">
            {t("viewCatalog")}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Event Feed */}
      {events.length > 0 && (
        <section className="border-b border-zinc-800 py-6 px-4" data-testid="events-section">
          <h2 className="font-display text-lg md:text-xl tracking-wider mb-4">{t("eventFeed")}</h2>
          <div className="space-y-3 max-w-3xl">
            {events.map((event) => (
              <div key={event.id} className="flex items-start gap-3 py-3 border-b border-zinc-800/50" data-testid={`event-${event.id}`}>
                <div className={`w-2 h-2 mt-1.5 shrink-0 ${event.type === "new_arrival" ? "bg-green-500" : event.type === "price_change" ? "bg-yellow-500" : event.type === "discount" ? "bg-red-500" : "bg-blue-500"}`} />
                {/* On desktop: image on the left, compact. On mobile: stacked */}
                {event.image ? (
                  <div className="flex-1 min-w-0 flex flex-col md:flex-row md:items-start gap-3">
                    <div className="w-full md:w-40 md:h-28 shrink-0 aspect-[16/9] md:aspect-auto overflow-hidden bg-zinc-950 relative">
                      <img src={getImageUrl(event.image, 800)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-display">{event.title}</p>
                      {event.description && <p className="text-xs text-zinc-500 mt-1 line-clamp-3">{event.description}</p>}
                      <p className="text-[10px] font-mono text-zinc-600 mt-1.5">
                        {new Date(event.created_at).toLocaleDateString("ru-RU", { day: "numeric", month: "short", year: "numeric" })}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-display">{event.title}</p>
                    {event.description && <p className="text-xs text-zinc-500 mt-1">{event.description}</p>}
                    <p className="text-[10px] font-mono text-zinc-600 mt-1.5">
                      {new Date(event.created_at).toLocaleDateString("ru-RU", { day: "numeric", month: "short", year: "numeric" })}
                    </p>
                  </div>
                )}
                {event.product_id && (
                  <Link to={`/product/${event.product_id}`} className="text-xs font-mono text-zinc-500 hover:text-white transition-colors shrink-0 mt-1">
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* New Arrivals Grid */}
      <section className="py-6 px-4" data-testid="new-arrivals-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg md:text-xl tracking-wider">{t("newArrivals")}</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 border border-zinc-800">
          {products.slice(0, 8).map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
        <Link to="/catalog"
          className="flex items-center justify-center w-full h-12 mt-px border border-zinc-800 text-sm font-display uppercase tracking-wider hover:bg-zinc-900 transition-colors"
          data-testid="view-all-btn">
          {t("viewAll")}
        </Link>
      </section>
    </div>
  );
}
